// Global connectivity signal for the game UI.
//
// One status ('online' | 'reconnecting' | 'offline') the whole app can read,
// derived from two independent signals:
//
//   1. navigator.onLine — the device's own view of reachability. Authoritative
//      for "fully offline" (airplane mode, wifi dropped) and never false-positives.
//   2. The Supabase realtime socket — every channel shares ONE websocket
//      (supabase.realtime). navigator.onLine stays TRUE when only that socket
//      drops (half-open wifi, backgrounded tab, server blip) — the exact failure
//      the per-channel reconnect in utils/realtime.js handles silently today.
//      Watching the shared socket surfaces it to the player.
//
// status:
//   'offline'      device reports no network                 (navigator.onLine === false)
//   'reconnecting' device online, but the shared realtime socket dropped while we
//                  still have active channels (Supabase is backing-off to re-open)
//   'online'       device online and socket healthy / idle / not yet used
//
// Two guards keep 'reconnecting' honest (no banner flashing):
//   - grace window: a normal screen change briefly closes the socket between
//     channels; we only surface 'reconnecting' if it stays closed past the grace.
//   - active-channel gate: a closed socket with zero subscribed channels is just
//     idle, not a drop, so it reads as 'online'.
//
// Analytics fire on TRANSITIONS only (not every poll), via @tasted/utils/analytics.

import { supabase } from '@tasted/utils/supabase';
import { track } from '@tasted/utils/analytics';

const RECONNECTING_GRACE_MS = 1500;

let online = typeof navigator === 'undefined' ? true : navigator.onLine;
let socketEverOpen = false;
let socketOpen = false;
let status = 'online';
let lostAt = 0; // timestamp we left 'online', for downtime analytics
let graceTimer = null;
let started = false;

const listeners = new Set();

function hasActiveChannels() {
  try {
    return (supabase.getChannels?.() || []).length > 0;
  } catch {
    return false;
  }
}

function compute() {
  if (!online) return 'offline';
  // A dropped socket only matters while we actually want channels connected.
  if (socketEverOpen && !socketOpen && hasActiveChannels()) return 'reconnecting';
  return 'online';
}

function evaluate() {
  const next = compute();
  if (next === status) return;
  const prev = status;
  status = next;

  // Transition analytics — never let a throw break the UI.
  try {
    if (next !== 'online') {
      if (prev === 'online') lostAt = Date.now();
      track('Connection Lost', { status: next });
    } else if (prev !== 'online') {
      track('Connection Restored', {
        from: prev,
        downtimeMs: lostAt ? Date.now() - lostAt : null,
      });
      lostAt = 0;
    }
  } catch {
    /* analytics must not break gameplay */
  }

  for (const cb of listeners) {
    try {
      cb();
    } catch {
      /* ignore individual subscriber errors */
    }
  }
}

function onOnline() {
  online = true;
  evaluate();
}

function onOffline() {
  online = false;
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
  evaluate();
}

function onSocketOpen() {
  socketEverOpen = true;
  socketOpen = true;
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
  evaluate();
}

function onSocketClose() {
  socketOpen = false;
  // Debounce: only surface 'reconnecting' if the socket stays closed past the
  // grace window (a quick close→reopen when switching screens shouldn't flash).
  if (graceTimer) clearTimeout(graceTimer);
  graceTimer = setTimeout(() => {
    graceTimer = null;
    evaluate();
  }, RECONNECTING_GRACE_MS);
}

/**
 * Register the window + realtime listeners exactly once. Safe to call repeatedly
 * (idempotent) and safe when realtime is the missing-env sentinel client.
 */
export function startConnectivity() {
  if (started) return;
  started = true;

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
  }

  try {
    const rt = supabase?.realtime;
    // Seed from the socket's current state if it's already up.
    if (rt?.isConnected?.()) {
      socketEverOpen = true;
      socketOpen = true;
    }
    rt?.onOpen?.(onSocketOpen);
    rt?.onClose?.(onSocketClose);
  } catch {
    /* realtime unavailable (sentinel) — fall back to navigator.onLine only */
  }

  status = compute();
}

export function getConnectivityStatus() {
  return status;
}

/**
 * Subscribe to status changes. Returns an unsubscribe fn. Starts the listeners
 * lazily on first subscription so nothing runs until the UI actually needs it.
 */
export function subscribeConnectivity(cb) {
  startConnectivity();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Test-only: reset module state between cases.
export function __resetConnectivity() {
  if (graceTimer) clearTimeout(graceTimer);
  graceTimer = null;
  online = typeof navigator === 'undefined' ? true : navigator.onLine;
  socketEverOpen = false;
  socketOpen = false;
  status = 'online';
  lostAt = 0;
  started = false;
  listeners.clear();
}
